import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { GiBull, GiBearFace } from 'react-icons/gi';
import { FaArrowUp, FaArrowDown, FaEquals, FaUndo, FaClock, FaTrophy, FaCheck, FaHourglassHalf } from 'react-icons/fa';
import { useT } from '../../../../i18n';
import { BetPanel, ClaimButton } from '../BettingComponents';
import CountdownTimer from '../CountdownTimer';
import { useBullvsBearContract } from '../../hooks/useBullvsBearContract';
import { useGetConfig, useGetRoundExtras, useChainOffset } from '../../context/PredictionsContext';
import {
  roundPool,
  sideMultiplier,
  nextMultiplier,
  roundOutcome,
  userPayout,
  priceSide,
  toUsdt,
  formatUsdt,
  formatPrice,
  formatMultiplier,
  REFUND_DELAY_SECONDS,
} from '../../utils/roundMath';

/** Seconds on the chain's clock, ticking */
export const useChainNow = () => {
  const offset = useChainOffset();
  const [now, setNow] = useState(() => (Date.now() + offset) / 1000);
  useEffect(() => {
    const id = setInterval(() => setNow((Date.now() + offset) / 1000), 1000);
    return () => clearInterval(id);
  }, [offset]);
  return now;
};

export const SIDE_STYLE = {
  up: { text: 'text-emerald-400', soft: 'bg-emerald-500/15 text-emerald-300 border-emerald-400/30', bar: 'bg-emerald-500', Icon: FaArrowUp, Animal: GiBull },
  down: { text: 'text-rose-400', soft: 'bg-rose-500/15 text-rose-300 border-rose-400/30', bar: 'bg-rose-500', Icon: FaArrowDown, Animal: GiBearFace },
  tie: { text: 'text-amber-300', soft: 'bg-amber-500/15 text-amber-200 border-amber-400/30', bar: 'bg-amber-500', Icon: FaEquals },
};

export const Badge = ({ className, children }) => (
  <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide ${className}`}>
    {children}
  </span>
);

const Header = ({ epoch, badge, right }) => (
  <div className="flex items-center justify-between gap-2">
    <div className="flex items-center gap-2">
      <span className="lb-num rounded-full bg-white/5 px-2.5 py-0.5 text-sm font-bold text-white/80">#{epoch}</span>
      {badge}
    </div>
    {right}
  </div>
);

/** Share of each side (house seed included) and what a winner gets per USDT */
export const PoolBar = ({ pool, multipliers, winner }) => {
  const t = useT();
  const upPct = Math.round(pool.upShare * 100);
  const sides = [
    ['up', t('bvb.up'), pool.up.stake, multipliers.up],
    ['down', t('bvb.down'), pool.down.stake, multipliers.down],
  ];
  return (
    <div className="space-y-2">
      <div className="flex h-2 overflow-hidden rounded-full bg-white/5">
        <div className="bg-emerald-500 transition-all duration-500" style={{ width: `${upPct}%` }} />
        <div className="flex-1 bg-rose-500 transition-all duration-500" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        {sides.map(([side, label, stake, mult]) => {
          const s = SIDE_STYLE[side];
          const dimmed = winner && winner !== side;
          return (
            <div
              key={side}
              className={`rounded-xl border px-3 py-2 ${s.soft} ${dimmed ? 'opacity-40' : ''} ${side === 'down' ? 'text-right' : ''}`}
            >
              <div className={`flex items-center gap-1 text-xs font-bold uppercase ${side === 'down' ? 'justify-end' : ''}`}>
                <s.Icon size={10} /> {label}
              </div>
              <div className={`mt-0.5 flex items-center gap-2 ${side === 'down' ? 'flex-row-reverse' : ''}`}>
                <s.Animal className={`h-7 w-7 shrink-0 ${s.text}`} aria-hidden="true" />
                <span className="lb-num text-lg font-extrabold leading-tight text-white">{formatMultiplier(mult)}</span>
              </div>
              <div className="lb-num text-[11px] text-white/50">{formatUsdt(stake)} USDT</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const InfoRow = ({ label, value, className = '' }) => (
  <div className="flex items-center justify-between text-xs">
    <span className="text-white/45">{label}</span>
    <span className={`lb-num font-semibold text-white/80 ${className}`}>{value}</span>
  </div>
);

/** The user's bet on this round */
export const PositionBox = ({ bet, children }) => {
  const t = useT();
  const s = SIDE_STYLE[bet.position];
  return (
    <div className="rounded-xl border border-brand/40 bg-brand/10 p-3">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-bold uppercase tracking-wide text-[#c9acea]">{t('bvb.yourPosition')}</span>
        <span className={`flex items-center gap-1 text-sm font-bold ${s.text}`}>
          <s.Animal className="h-5 w-5" aria-hidden="true" /> {bet.position === 'up' ? t('bvb.up') : t('bvb.down')} ·{' '}
          <span className="lb-num">{formatUsdt(toUsdt(bet.deposit ?? bet.amount))}</span>
        </span>
      </div>
      {children && <div className="mt-2 space-y-1.5">{children}</div>}
    </div>
  );
};

/* ---------- live: the round being decided ---------- */

const LiveCard = ({ round, epoch, oraclePrice, pool, config }) => {
  const t = useT();
  const now = useChainNow();
  const { useUserBet } = useBullvsBearContract();
  const bet = useUserBet(epoch);
  const lock = Number(round.lockPrice);
  const price = Number(oraclePrice);
  const side = priceSide(price, lock);
  const delta = price && lock ? price - lock : 0;
  const deltaPct = lock ? (delta / lock) * 100 : 0;
  const s = SIDE_STYLE[side || 'tie'];
  const lockTs = Number(round.lockTimestamp);
  const closeTs = Number(round.closeTimestamp);
  const progress = closeTs > lockTs ? Math.min(1, Math.max(0, (now - lockTs) / (closeTs - lockTs))) : 0;
  const overdue = closeTs && now > closeTs;
  const willRefund = closeTs && now > closeTs + REFUND_DELAY_SECONDS;
  const multipliers = { up: sideMultiplier(pool, 'up', config), down: sideMultiplier(pool, 'down', config) };

  return (
    <div className="flex h-full flex-col gap-3">
      <Header
        epoch={epoch}
        badge={
          <Badge className="border-amber-400/40 bg-amber-500/15 text-amber-300">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-amber-300" /> {t('bvb.live')}
          </Badge>
        }
        right={
          <span className="flex items-center gap-1.5 text-xs text-white/50">
            <FaClock size={11} /> {overdue ? t('bvb.awaiting') : <CountdownTimer target={closeTs} className="text-white" />}
          </span>
        }
      />

      <div className="relative isolate overflow-hidden rounded-2xl border border-white/10 bg-black/40 p-4 text-center">
        {s.Animal && (
          <s.Animal
            aria-hidden="true"
            className={`pointer-events-none absolute -right-4 -top-3 -z-10 h-28 w-28 opacity-[0.12] transition-colors duration-500 ${s.text}`}
          />
        )}
        <div className="relative text-[11px] uppercase tracking-wider text-white/40">{t('bvb.currentPrice')}</div>
        <div className={`lb-num mt-1 text-3xl font-extrabold ${s.text}`}>{formatPrice(price)}</div>
        <div className={`mt-2 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-bold ${s.soft}`}>
          <s.Icon size={10} />
          <span className="lb-num">
            {delta >= 0 ? '+' : '−'}${Math.abs(delta).toFixed(2)} ({deltaPct >= 0 ? '+' : '−'}
            {Math.abs(deltaPct).toFixed(3)}%)
          </span>
        </div>
        <div className="mt-3 text-xs text-white/45">
          {t('bvb.lockPrice')} <span className="lb-num font-semibold text-white/70">{formatPrice(lock)}</span>
        </div>
        <div className="mt-3 h-1 overflow-hidden rounded-full bg-white/10">
          <div className="h-full bg-gradient-to-r from-brand to-fuchsia-400 transition-all duration-1000" style={{ width: `${progress * 100}%` }} />
        </div>
      </div>

      {overdue && (
        <div className="rounded-xl border border-orange-400/30 bg-orange-500/10 px-3 py-2 text-xs text-orange-200">
          <FaHourglassHalf className="mr-1 inline" />
          {willRefund ? t('bvb.refundSoon') : t('bvb.overdueBody')}
        </div>
      )}

      <PoolBar pool={pool} multipliers={multipliers} winner={side === 'tie' ? null : side} />
      <InfoRow label={t('bvb.totalPool')} value={`${formatUsdt(pool.total)} USDT`} />
      {pool.house > 0 && <InfoRow label={t('bvb.houseSeed')} value={`${formatUsdt(pool.house)} USDT`} />}

      {bet && (
        <PositionBox bet={bet}>
          <InfoRow
            label={t('bvb.status')}
            value={side === bet.position ? t('bvb.winningNow') : side === 'tie' ? t('bvb.tie') : t('bvb.losingNow')}
            className={side === bet.position ? 'text-emerald-300' : 'text-rose-300'}
          />
          <InfoRow
            label={t('bvb.ifYouWin')}
            value={`≈ ${formatUsdt(toUsdt(bet.amount) * multipliers[bet.position])} USDT`}
          />
        </PositionBox>
      )}
    </div>
  );
};

/* ---------- next: open for bets ---------- */

const NextCard = ({ round, epoch, pool, config, extras, referralAddress }) => {
  const t = useT();
  const { useUserBet } = useBullvsBearContract();
  const bet = useUserBet(epoch);
  const multipliers = { up: nextMultiplier(pool, 'up', config), down: nextMultiplier(pool, 'down', config) };
  const earlyBirdLeft = extras.earlyBirds !== undefined ? Math.max(0, config.earlyBirdCount - extras.earlyBirds) : 0;
  const betMultipliers = { up: sideMultiplier(pool, 'up', config), down: sideMultiplier(pool, 'down', config) };

  return (
    <div className="flex h-full flex-col gap-3">
      <Header
        epoch={epoch}
        badge={<Badge className="border-sky-400/40 bg-sky-500/15 text-sky-300">{t('bvb.next')}</Badge>}
        right={
          <span className="flex items-center gap-1.5 text-xs text-white/50">
            {t('bvb.locksIn')} <CountdownTimer target={round.lockTimestamp} className="text-white" />
          </span>
        }
      />

      <PoolBar pool={pool} multipliers={multipliers} />

      <div className="flex flex-wrap gap-1.5">
        {pool.house > 0 && (
          <Badge className="border-white/10 bg-white/5 normal-case text-white/70">
            {t('bvb.houseSeed')}: <span className="lb-num">{formatUsdt(pool.house)}</span>
          </Badge>
        )}
        {earlyBirdLeft > 0 && (
          <Badge className="border-amber-400/30 bg-amber-500/10 normal-case text-amber-200">
            {t('bvb.earlyBirdSlots', { n: earlyBirdLeft, bonus: config.earlyBirdBonusBps / 100 })}
          </Badge>
        )}
      </div>

      <div className="mt-auto">
        {bet ? (
          <PositionBox bet={bet}>
            <InfoRow label={t('bvb.ifYouWin')} value={`≈ ${formatUsdt(toUsdt(bet.amount) * betMultipliers[bet.position])} USDT`} />
            <p className="text-[11px] text-white/45">{t('bvb.waitingStart')}</p>
          </PositionBox>
        ) : (
          <BetPanel epoch={epoch} pool={pool} config={config} earlyBirdLeft={earlyBirdLeft} referralAddress={referralAddress} />
        )}
        <p className="mt-2 text-center text-[11px] text-white/35">{t('bvb.feeNote', { fee: config.feeBps / 100 })}</p>
      </div>
    </div>
  );
};

/* ---------- later: not open yet ---------- */

const LaterCard = ({ epoch, opensAt }) => {
  const t = useT();
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
      <div className="flex items-center gap-3 opacity-40" aria-hidden="true">
        <GiBull className="h-10 w-10 text-emerald-400" />
        <span className="lb-heading text-sm text-white/60">vs</span>
        <GiBearFace className="h-10 w-10 text-rose-400" />
      </div>
      <span className="lb-num rounded-full bg-white/5 px-2.5 py-0.5 text-sm font-bold text-white/60">#{epoch}</span>
      <Badge className="border-white/10 bg-white/5 text-white/50">{t('bvb.later')}</Badge>
      <div className="text-xs text-white/40">{t('bvb.opensIn')}</div>
      <CountdownTimer target={opensAt} variant="boxes" />
    </div>
  );
};

/* ---------- expired: settled (or waiting to be) ---------- */

const OUTCOME_BADGE = {
  up: ['border-emerald-400/40 bg-emerald-500/15 text-emerald-300', FaArrowUp, 'bvb.up'],
  down: ['border-rose-400/40 bg-rose-500/15 text-rose-300', FaArrowDown, 'bvb.down'],
  tie: ['border-amber-400/40 bg-amber-500/15 text-amber-200', FaEquals, 'bvb.tie'],
  refund: ['border-orange-400/40 bg-orange-500/15 text-orange-200', FaUndo, 'bvb.refund'],
  refund_pending: ['border-orange-400/40 bg-orange-500/15 text-orange-200', FaHourglassHalf, 'bvb.awaiting'],
  awaiting: ['border-white/15 bg-white/5 text-white/60', FaHourglassHalf, 'bvb.awaiting'],
};

export const ExpiredCard = ({ round, epoch, pool, config, extras }) => {
  const t = useT();
  const now = useChainNow();
  const { useUserBet } = useBullvsBearContract();
  const bet = useUserBet(epoch);
  const outcome = roundOutcome(round, extras, now);
  const [badgeClass, Icon, labelKey] = OUTCOME_BADGE[outcome];
  const lock = Number(round.lockPrice);
  const close = Number(round.closePrice);
  const settledSide = outcome === 'up' || outcome === 'down' ? outcome : null;
  const reward = toUsdt(round.rewardAmount);
  const base = toUsdt(round.rewardBaseCalAmount);
  // a finished round only pays its winning side (nothing on a tie or a refund)
  const multipliers = {
    up: settledSide === 'up' && base > 0 ? reward / base : 0,
    down: settledSide === 'down' && base > 0 ? reward / base : 0,
  };
  const payout = userPayout(round, extras, bet, outcome);
  const noWinners = (outcome === 'tie' || (settledSide && !(reward > 0))) && round.oracleCalled;

  let result = null;
  if (bet && round.oracleCalled) {
    if (outcome === 'refund') result = ['refund', t('bvb.refundOf'), payout];
    else if (payout > 0) result = ['won', t('bvb.youWon'), payout];
    else result = ['lost', t('bvb.youLost'), toUsdt(bet.deposit ?? bet.amount)];
  }

  return (
    <div className="flex h-full flex-col gap-3">
      <Header
        epoch={epoch}
        badge={
          <Badge className={badgeClass}>
            <Icon size={10} /> {t(labelKey)}
          </Badge>
        }
        right={result && result[0] === 'won' ? <FaTrophy className="text-amber-300" /> : null}
      />

      <div className="space-y-1.5 rounded-xl border border-white/10 bg-black/30 p-3">
        <InfoRow label={t('bvb.lockPrice')} value={formatPrice(lock)} />
        <InfoRow
          label={t('bvb.closePrice')}
          value={round.oracleCalled ? formatPrice(close) : '—'}
          className={settledSide ? SIDE_STYLE[settledSide].text : ''}
        />
        {round.oracleCalled && lock > 0 && close > 0 && (
          <InfoRow
            label={t('bvb.change')}
            value={`${close >= lock ? '+' : '−'}$${Math.abs(close - lock).toFixed(2)}`}
            className={settledSide ? SIDE_STYLE[settledSide].text : ''}
          />
        )}
      </div>

      <PoolBar pool={pool} multipliers={multipliers} winner={settledSide} />
      <InfoRow label={t('bvb.totalPool')} value={`${formatUsdt(pool.total)} USDT`} />

      {outcome === 'refund_pending' && <p className="text-xs text-orange-200/80">{t('bvb.refundSoon')}</p>}
      {outcome === 'refund' && <p className="text-xs text-orange-200/80">{t('bvb.refundBody', { fee: config.feeBps / 100 })}</p>}
      {noWinners && <p className="text-xs text-white/45">{t('bvb.noWinners')}</p>}

      {bet && (
        <div className="mt-auto">
          <PositionBox bet={bet}>
            {result && (
              <InfoRow
                label={result[1]}
                value={`${result[0] === 'lost' ? '−' : '+'}${formatUsdt(result[2])} USDT`}
                className={result[0] === 'won' ? 'text-emerald-300' : result[0] === 'refund' ? 'text-orange-200' : 'text-rose-300'}
              />
            )}
            {result && result[0] !== 'lost' &&
              (bet.claimed ? (
                <p className="flex items-center gap-1 text-xs font-semibold text-emerald-300">
                  <FaCheck /> {t('bvb.claimedDone')}
                </p>
              ) : (
                <ClaimButton epoch={epoch} amount={result[2]} isRefund={result[0] === 'refund'} />
              ))}
          </PositionBox>
        </div>
      )}
    </div>
  );
};

const RoundCard = ({ round, epoch, status, oraclePrice, referralAddress, opensAt }) => {
  const config = useGetConfig();
  const extras = useGetRoundExtras(epoch);
  const pool = roundPool(round, extras);
  const live = status === 'live';
  const next = status === 'next';

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className={`h-full rounded-2xl border p-4 backdrop-blur-sm transition-colors ${
        live
          ? 'border-brand bg-gradient-to-b from-[#1c0e29] to-[#06030a] shadow-[0_0_40px_-12px_rgba(142,63,204,0.7)]'
          : next
            ? 'border-sky-400/40 bg-gradient-to-b from-[#1e1428] to-[#06030a]'
            : status === 'later'
              ? 'border-white/5 bg-[#06030a]/80'
              : 'border-white/10 bg-[#06030a]/90'
      }`}
    >
      {status === 'live' && <LiveCard round={round} epoch={epoch} oraclePrice={oraclePrice} pool={pool} config={config} />}
      {status === 'next' && (
        <NextCard round={round} epoch={epoch} pool={pool} config={config} extras={extras} referralAddress={referralAddress} />
      )}
      {status === 'later' && <LaterCard epoch={epoch} opensAt={opensAt} />}
      {status === 'expired' && <ExpiredCard round={round} epoch={epoch} pool={pool} config={config} extras={extras} />}
    </motion.div>
  );
};

export default RoundCard;
